import { describeMutation } from '@siteless/resource';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import CurrentUserResource from './CurrentUser';
import { auth, database } from '../../config/firebase';
import { CurrentUserMustResource } from './CurrentUserMust';
import { BrowserStorageResource, writeBrowserStorage } from '../BrowserStorage';
import AllUserProfilesResource from '../AllUserProfiles';

export const acceptAuthChangeMutation = describeMutation(
  'acceptAuthChange',
  async () => {
    await CurrentUserResource.refresh();
    let currentUser = await CurrentUserResource.UNSTABLE__getFromStoreOrGet(
      undefined,
    );
    if (currentUser?.authProvider) {
      writeBrowserStorage((last) => ({
        ...last,
        lastLoginMethod: currentUser.authProvider,
      }));
      await BrowserStorageResource.refresh(undefined);
    }
  },
);

export const logOutMutation = describeMutation('logOut', async () => {
  await CurrentUserResource.yield(undefined, () => null);
  await auth.signOut();
});

export const updateUserPropertiesMutation = describeMutation(
  'updateUserProperties',
  async ({
    userId,
    handle,
    avatarId,
    avatarBackgroundHex,
    prefersLeftHandMode,
    prefersLanguageKey,
    letterboxdId,
    spoilerDelayMs,
  }) => {
    const updates = {};
    if (handle !== undefined) updates.handle = handle.toLowerCase().trim();
    if (avatarId !== undefined) updates.avatarId = avatarId;
    if (avatarBackgroundHex !== undefined)
      updates.avatarBackgroundHex = avatarBackgroundHex;
    if (prefersLeftHandMode !== undefined)
      updates.prefersLeftHandMode = prefersLeftHandMode;
    if (prefersLanguageKey !== undefined)
      updates.prefersLanguageKey = prefersLanguageKey;
    if (letterboxdId !== undefined) updates.letterboxdId = letterboxdId;
    if (spoilerDelayMs !== undefined) updates.spoilerDelayMs = spoilerDelayMs;

    await CurrentUserResource.yield(undefined, (identity, last) => {
      if (last === null) return last;
      return {
        ...last,
        ...updates,
      };
    });
    await CurrentUserMustResource.yield(undefined, (identity, last) => {
      if (last === null) return last;
      return {
        ...last,
        ...updates,
      };
    });
    await AllUserProfilesResource.yield(undefined, (identity, last) => {
      if (last === null) return last;

      return {
        ...last,
        users: {
          ...last.users,
          [userId]: {
            ...(last.users[userId] ? last.users[userId] : { id: userId }),
            ...updates,
          },
        },
      };
    });

    try {
      await setDoc(doc(database, 'users', userId), updates, { merge: true });
    } catch (error) {
      window.alert(error);
      await CurrentUserResource.refresh(undefined);
      await CurrentUserMustResource.refresh(undefined);
    }
  },
);

export const consentToNotificationForYearMutation = describeMutation(
  'consentToNotificationForYear',
  async ({ userId, year, consentStatus }) => {
    let updates = {
      consentedToNotificationForYears: {
        [year]: consentStatus,
      },
      ...(consentStatus && { optedOutCommunication: false }),
    };

    await CurrentUserResource.yield(undefined, (identity, last) => {
      if (last === null) return last;
      return {
        ...last,
        consentedToNotificationForYears: {
          ...last.consentedToNotificationForYears,
          [year]: consentStatus,
        },
        ...(consentStatus && { optedOutCommunication: false }),
      };
    });

    await CurrentUserMustResource.yield(undefined, (identity, last) => {
      if (last === null) return last;
      return {
        ...last,
        consentedToNotificationForYears: {
          ...last.consentedToNotificationForYears,
          [year]: consentStatus,
        },
        ...(consentStatus && { optedOutCommunication: false }),
      };
    });

    try {
      await setDoc(doc(database, 'users', userId), updates, { merge: true });
    } catch (error) {
      window.alert(error);
      await CurrentUserResource.refresh(undefined);
      await CurrentUserMustResource.refresh(undefined);
    }
  },
);

export const setHiddenActivityKeyMutation = describeMutation(
  'setHiddenActivityKey',
  async ({ userId, activityKey, hidden }) => {
    await CurrentUserResource.yield(undefined, (identity, last) => {
      if (last === null) return last;
      return {
        ...last,
        hiddenActivityKeys: {
          ...last.hiddenActivityKeys,
          [activityKey]: hidden,
        },
      };
    });

    await CurrentUserMustResource.yield(undefined, (identity, last) => {
      if (last === null) return last;
      return {
        ...last,
        hiddenActivityKeys: {
          ...last.hiddenActivityKeys,
          [activityKey]: hidden,
        },
      };
    });

    try {
      await setDoc(
        doc(database, 'users', userId),
        {
          hiddenActivityKeys: {
            [activityKey]: hidden,
          },
        },
        { merge: true },
      );
    } catch (error) {
      window.alert(error);
      await CurrentUserResource.refresh(undefined);
      await CurrentUserMustResource.refresh(undefined);
    }
  },
);

export const saveHandleAfterViolationMutation = describeMutation(
  'saveHandleAfterViolation',
  async ({ userId, handle }) => {
    const normalizedHandle = handle.toLowerCase().trim();

    await setDoc(
      doc(database, 'users', userId),
      { handle: normalizedHandle },
      { merge: true },
    );

    const maxAttempts = 10;
    const intervalMs = 500;
    for (let i = 0; i < maxAttempts; i++) {
      const userDoc = await getDoc(doc(database, 'users', userId));
      const data = userDoc.data();
      if (!data?.handleViolation) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    await CurrentUserResource.refresh(undefined);
    await CurrentUserMustResource.refresh(undefined);
  },
);
